import React from 'react'
import ReactDOM from 'react-dom'
import { WithTranslation, withTranslation } from 'react-i18next';

import Modal from './Modal'
import FieldString from './FieldString';
import InputButton from './InputButton';
import { MdSave, MdArrowDropDown } from 'react-icons/md';
import Loader from './Loader';

type ModalSaveInternalProps = {
  isOpen: boolean
  onOpenToggle(...args: unknown[]): unknown
  onStyleOpen(...args: unknown[]): unknown
  onStyleSave(...args: unknown[]): unknown
  customStyles: any[]
} & WithTranslation;

type ModalSaveState = {
  fileName: string
  error?: string | null
  loading: boolean
  dropdownOpen: boolean
  saveMode: 'existing' | 'new'
  dropdownPosition: { top: number; left: number; width: number } | null
};

const styleFromWindow = () => {
  const styleId = window.localStorage.getItem("atlas-studio:latest_style");
  const styleItem = window.localStorage.getItem(`atlas-studio:style:${styleId}`);
  // Nothing is stored until the first style has loaded, and this runs on
  // every render, also while the modal is closed.
  if (!styleItem) return {};
  const obj = JSON.parse(styleItem);
  return obj;
};

class ModalSaveInternal extends React.Component<ModalSaveInternalProps, ModalSaveState> {
  private buttonRef = React.createRef<HTMLDivElement>();

  constructor(props: ModalSaveInternalProps) {
    super(props);
    this.state = {
      fileName: "",
      loading: false,
      dropdownOpen: false,
      saveMode: 'existing',
      dropdownPosition: null
    };

    this.setStyleName = this.setStyleName.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  onOpenToggle() {
    this.setState({
      fileName: "",
      error: null,
      dropdownOpen: false,
      saveMode: 'existing',
      dropdownPosition: null
    });
    this.props.onOpenToggle();
  }

  toggleDropdown() {
    if (!this.state.dropdownOpen && this.buttonRef.current) {
      const rect = this.buttonRef.current.getBoundingClientRect();
      this.setState({
        dropdownOpen: true,
        dropdownPosition: {
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width
        }
      });
    } else {
      this.setState({ dropdownOpen: false, dropdownPosition: null });
    }
  }

  handleClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isInsideDropdown = target.closest('.save-dropdown-container');
    const isInsideButton = target.closest('.save-dropdown-button-container');
    if (!isInsideDropdown && !isInsideButton) {
      this.setState({ dropdownOpen: false, dropdownPosition: null });
    }
  }

  // A style that is not one of the user's saved styles (e.g. one picked from the gallery) can only be
  // saved as new, so open the dialog in that mode instead of offering to overwrite a file.
  componentDidUpdate(prevProps: ModalSaveInternalProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      const name = (styleFromWindow().name || "").trim();
      this.setState({ saveMode: this.isExistingName(name) ? 'existing' : 'new', error: null });
    }
  }

  isExistingName(name: string) {
    return (this.props.customStyles || []).some((file: string) => {
      // Extract filename from path format (e.g., "userId/filename" -> "filename")
      const parts = file.split("/");
      const existingFileName = (parts.length > 1 ? parts[1] : file).replace(/\.json$/, '');
      return existingFileName.trim() === name;
    });
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  clearError() {
    this.setState({ error: null });
  }

  setStyleName(styleName: string) {
    this.setState({
      fileName: styleName,
    });
  }
  handleSave = async () => {
    // spaces around a name are almost always a typo, and they end up in the file name
    const fileName = (this.state.fileName || styleFromWindow().name || "").trim();
    if (!fileName) {
      this.setState({ error: "Give your style a name first." });
      return;
    }

    // Check if saving as new and file already exists
    if (this.state.saveMode === 'new') {
      if (this.isExistingName(fileName)) {
        this.setState({
          error: "A file with this name already exists. Please choose a different name.",
        });
        return;
      }
    }

    this.setState({ loading: true });
    try {
      const response = await this.props.onStyleSave(fileName) as { success: boolean; message?: string };
      if (response?.success) {
        this.props.onOpenToggle();
      } else {
        this.setState({
          error: response?.message?.toString() || "The style could not be saved. Please try again.",
        });
        return;
      }
    } catch (error) {
      console.error("Error saving style:", error);
      this.setState({ error: "The style could not be saved. Please try again." });
    } finally {
      this.setState({ loading: false });
    }
  };
  render() {
    let errorElement;
    if (this.state.error) {
      errorElement = (
        <div className="studio-modal-error">
          {this.state.error}
          <a
            href="#"
            onClick={() => this.clearError()}
            className="studio-modal-error-close"
          >
            ×
          </a>
        </div>
      );
    }

    return  (
      <div>
        <Modal
          data-wd-key="modal:save"
          isOpen={this.props.isOpen}
          onOpenToggle={() => this.onOpenToggle()}
          title={'Save style'}
        >
          {errorElement}
          <p style={{ fontSize: '13px', color: 'var(--studio-muted)', marginBottom: '8px', marginTop: '4px' }}>
            {this.state.saveMode === 'existing'
              ? `You are about to save as the existing file "${this.state.fileName || styleFromWindow().name}"`
              : `You are about to save this as a new style "${this.state.fileName || styleFromWindow().name}"`
            }
          </p>
          <FieldString
            label={'Style Name'}
            fieldSpec={{ doc: 'This will be the name of your file displayed in portal'}}
            value={this.state.fileName || styleFromWindow().name}
            onChange={(value) => {
              this.setStyleName(value as string);
              this.clearError();
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault(); // Prevent default form submission
                this.setState(
                  { fileName: (e.target as HTMLInputElement).value }, // Update the state with the current input value
                  () => {
                    this.handleSave(); // Call handleSave after state is updated
                  }
                );
              }
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', marginRight: 12, marginTop: 12 }}>
            <div ref={this.buttonRef} className="save-dropdown-button-container" style={{ position: 'relative', display: 'inline-block' }}>
              <div style={{ display: 'flex' }}>
                <InputButton
                  className="studio-button--primary"
                  style={{
                    minWidth: '110px',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    paddingRight: '8px'
                  }}
                  onClick={this.handleSave}
                  disabled={this.state.loading}
                >
                  {this.state.loading ? (
                    <Loader className="" />
                  ) : (
                    <>
                      <MdSave style={{ marginRight: '4px' }} />
                      {this.state.saveMode === 'existing' ? 'Save as Existing' : 'Save as New'}
                    </>
                  )}
                </InputButton>
                <InputButton
                  className="studio-button--primary"
                  style={{
                    width: '24px',
                    minWidth: '24px',
                    padding: '0',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: '1px solid rgba(0,0,0,0.1)'
                  }}
                  onClick={this.toggleDropdown}
                  disabled={this.state.loading}
                >
                  <MdArrowDropDown />
                </InputButton>
              </div>
            </div>
          </div>
          {this.state.dropdownOpen && this.state.dropdownPosition && ReactDOM.createPortal(
            <div
              className="save-dropdown-container"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: this.state.dropdownPosition.top + 'px',
                left: this.state.dropdownPosition.left + 'px',
                minWidth: this.state.dropdownPosition.width + 'px',
                backgroundColor: 'var(--studio-field)',
                border: '1px solid #475569',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                zIndex: 10000
              }}>
              <div
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: this.state.saveMode === 'existing' ? 'var(--studio-border)' : 'var(--studio-field)',
                  color: 'var(--studio-text)',
                  borderRadius: '4px 4px 0 0',
                  fontSize: '12px',
                  whiteSpace: 'nowrap'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  this.setState({ saveMode: 'existing', dropdownOpen: false, dropdownPosition: null });
                  this.clearError();
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--studio-border)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = this.state.saveMode === 'existing' ? 'var(--studio-border)' : 'var(--studio-field)'}
              >
                Save as Existing
              </div>
              <div
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: this.state.saveMode === 'new' ? 'var(--studio-border)' : 'var(--studio-field)',
                  color: 'var(--studio-text)',
                  borderRadius: '0 0 4px 4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  this.setState({ saveMode: 'new', dropdownOpen: false, dropdownPosition: null });
                  this.clearError();
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--studio-border)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = this.state.saveMode === 'new' ? 'var(--studio-border)' : 'var(--studio-field)'}
              >
                Save as New
              </div>
            </div>,
            document.body
          )}
        </Modal>
      </div>
    )
  }
}

const ModalSave = withTranslation()(ModalSaveInternal);
export default ModalSave;
